import { PhoneNumberType } from '@/models';

export enum Gender {
  MALE = 'M',
  FEMALE = 'F',
  UNKNOWN = 'U',
  // For MasterName only
  NOT_REPORTED = 'R',
  NOT_APPLICABLE = 'X',
}

export const GenderList = {
  [Gender.MALE]: 'Male',
  [Gender.FEMALE]: 'Female',
  [Gender.UNKNOWN]: 'Unknown',
} as const;

export enum Race {
  WHITE = 'W',
  BLACK_OR_AFRICAN_AMERICAN = 'B',
  AMERICAN_INDIAN_OR_ALASKA_NATIVE = 'I',
  ASIAN = 'A',
  NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER = 'P',
  UNKNOWN = 'U', // Unknown
  NOT_REPORTED = 'R',
  NOT_APPLICABLE = 'X',
}

export const RaceList = {
  [Race.WHITE]: 'White',
  [Race.BLACK_OR_AFRICAN_AMERICAN]: 'Black or African American',
  [Race.AMERICAN_INDIAN_OR_ALASKA_NATIVE]: 'American Indian or Alaska Native',
  [Race.ASIAN]: 'Asian',
  [Race.NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER]:
    'Native Hawaiian or Other Pacific Islander',
  [Race.NOT_REPORTED]: 'Not Reported',
  [Race.UNKNOWN]: 'Unknown',
} as const;

export enum EthnicOrigin {
  HISPANIC_LATINO = 'H',
  NOT_HISPANIC_LATINO = 'N',
  NOT_REPORTED = 'R',
  UNKNOWN = 'U',
  NOT_APPLICABLE = 'X',
}

export const EthnicOriginList = {
  [EthnicOrigin.HISPANIC_LATINO]: 'Hispanic or Latino',
  [EthnicOrigin.NOT_HISPANIC_LATINO]: 'Not Hispanic or Latino',
  [EthnicOrigin.NOT_REPORTED]: 'Not reported',
  [EthnicOrigin.UNKNOWN]: 'Unknown',
} as const;

// Residence Status values - supports both numeric codes (WITNESS/VICTIM) and letter codes (OFFENDER)
export const ResidenceStatusList = {
  // Numeric codes for WITNESS/VICTIM involvement
  '01': 'Resident',
  '02': 'Commuter',
  '03': 'Tourist',
  '04': 'Military',
  '05': 'Student',
  '06': 'Temporary Resident/Foreign National',
  '07': 'Other Status',
  '08': 'Homeless',
  '77': 'Not Applicable',
  '88': 'Not Reported',
  '99': 'Unknown',
  // Letter codes for OFFENDER involvement
  R: 'Resident',
  N: 'Nonresident',
  F: 'Foreign Nonresident',
  U: 'Unknown',
} as const;

export const EyeColor = {
  BLK: 'BLACK',
  BLU: 'BLUE',
  BRO: 'BROWN',
  GRY: 'GRAY',
  GRN: 'GREEN',
  HAZ: 'HAZEL',
  MAR: 'MAROON',
  PNK: 'PINK',
  MUL: 'MULTI-COLORED',
  XXX: 'UNKNOWN',
  OTH: 'OTHER',
} as const;

export const MaritalStatus = {
  '1': 'Legally Married',
  '2': 'Single (never married)',
  '3': 'Divorced',
  '4': 'Widowed',
  '5': 'Separated',
  '6': 'Living Together/Common Law',
  '9': 'Unknown',
} as const;

export const ReligionList = {
  '100': 'Protestant',
  '200': 'Catholic',
  '300': 'Jewish',
  '400': 'Islam',
  '500': 'Other',
  '600': 'None',
  '999': 'Unknown',
} as const;

export const EducationList = {
  '01': 'High School Equivalency Diploma',
  '02': 'High School Diploma',
  '03': '1 Year College Certificate',
  '04': 'Associate Degree',
  '05': 'Bachelors Degree',
  '06': 'Masters Degree',
  '07': 'Doctoral Degree',
  '08': 'Other College Degree',
  '09': 'No Degree',
  '21': 'Test education',
  '99': 'Unknown',
} as const;

export const HairColor = {
  BAL: 'Bald',
  BLK: 'Black',
  BLN: 'Blonde',
  BRO: 'Brown',
  GRY: 'Gray',
  SDY: 'Sandy',
  WHI: 'White',
  RED: 'Red',
  XXX: 'Unknown',
  OTH: 'Other',
  BLU: 'Blue',
  GRN: 'Green',
  ONG: 'Orange',
  PNK: 'Pink',
  PLE: 'Purple',
} as const;

export const CountryList = {
  OC: 'MACAU (FORMERLY MACAO)',
  OF: 'NORFOLK ISLAND, TERRITORY OF (AUSTRALIAN EXTERNAL TERRITORY)',
  OI: 'OKINAWA',
  OM: 'OMAN',
  AO: 'ANGOLA',
  AA: 'ALBANIA',
  AD: 'ANDORRA',
  AE: 'ANGUILLA (FORMERLY ST. KITTS-ANGUILLA, ST. KITTS - AW)',
  AF: 'AFGHANISTAN',
  AH: 'ASHMORE AND CARTIER ISLANDS, TERRITORY OF (AUSTRALIAN EXTERNAL TERRITORY)',
  AI: 'ANTIGUA AND BARBUDA (FORMERLY ANTIGUA)',
  AJ: 'ARUBA (NOW INDEPENDENT OF NETHERLANDS ANTILLES)',
  AN: 'ALGERIA',
  AP: 'ARMENIA',
  AQ: 'AZORES ISLANDS',
  AS: 'AUSTRALIA (PRE 1995 RES BOAT FILE CODE FOR AMERICAN SAMOA, SEE AM)',
  AT: 'ARGENTINA',
  AU: 'AUSTRIA',
  AV: 'AZERBAIJAN',
  BO: 'BRITISH INDIAN OCEAN TERRITORY (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  BB: 'BARBADOS',
  BD: 'BAHAMAS, THE',
  BE: 'BAHRAIN/BAHREIN',
  BF: 'BASSAS DA INDIA (FRENCH POSSESSION)',
  BG: 'BELGIUM',
  BH: 'BELIZE (FORMERLY BRITISH HONDURAS)',
  BI: 'BURUNDI',
  BL: 'BANGLADESH',
  BM: 'BERMUDA',
  BN: 'BHUTAN',
  BP: 'BOSNIA AND HERZEGOVENIA (HERZEGOVENIA HC REFERENCE ONLY)',
  BQ: 'BOUVET ISLAND (NORWEGIAN TERRITORY)',
  BR: 'BURMA',
  BS: 'SOLOMON ISLANDS (FORMERLY BRITISH SOLOMON ISLANDS)',
  BT: 'BOTSWANA',
  BU: 'BULGARIA',
  BV: 'BOLIVIA',
  BW: 'BALEARIC ISLANDS',
  BX: 'BRUNEI',
  BY: 'BELARUS',
  BZ: 'BRAZIL',
  CB: 'COLOMBIA, REPUBLIC OF',
  CC: 'CUBA, REPUBLIC OF',
  CD: 'CANADA (USE WHEN PROVINCE IS NOT LISTED)',
  CF: 'CHAD (PRE 1995 RES BOAT FILE CODE FOR CALIFORNIA, SEE CA)',
  CJ: 'CAMBODIA (FORMERLY KHMER REPUBLIC AND KAMPUCHEA)',
  CM: 'CAMEROON',
  CP: 'CAYMAN ISLANDS (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  CQ: 'CHILE',
  CR: 'COSTA RICA',
  CS: 'CYPRUS, REPUBLIC OF',
  CV: 'CAPE VERDE ISLANDS',
  CW: 'CENTRAL AFRICAN REPUBLIC',
  CY: 'SRI LANKA (FORMERLY CEYLON)',
  DB: 'CLIPPERTON ISLAND (FRENCH POSSESSION)',
  DD: 'COCOS (KEELING) ISLANDS (AUSTRALIAN DEPENDENCY)',
  DG: 'COMOROS (FEDERAL ISLAMIC REPUBLIC OF THE)',
  DH: 'BENIN (FORMERLY DAHOMEY)',
  DI: 'COOK ISLANDS',
  DJ: 'CORAL SEA ISLANDS (AUSTRALIAN TERRITORY)',
  DK: 'DENMARK, KINGDOM OF',
  DM: 'DOMINICA',
  DN: 'DJIBOUTI, REPUBLIC OF',
  DR: 'DOMINICAN REPUBLIC',
  EO: 'ETHIOPIA',
  EK: 'EQUATORIAL GUINEA',
  EL: 'EL SALVADOR',
  EN: 'ENGLAND (ALSO UN FOR UNITED KINGDOM)',
  ER: 'EUROPA ISLAND (FRENCH POSSESSION)',
  ES: 'ESTONIA',
  ET: 'ERITREA',
  EU: 'ECUADOR',
  EY: 'EGYPT (FORMERLY UNITED ARAB REPUBLIC - UA REFERENCE ONLY)',
  EZ: 'CZECH REPUBLIC',
  FO: 'FAROE ISLANDS',
  FA: 'FALKLAND ISLANDS, UK DEPENDENCY (ISLAS MALVINAS)',
  FD: 'FINLAND',
  FG: 'FRENCH GUIANA (DEPARTMENT OF FRANCE)',
  FJ: 'FIJI',
  FN: 'FRANCE',
  FP: 'FRENCH POLYNESIA, TERRITORY OF (FRENCH OVERSEAS TERRITORY)',
  FR: 'FRENCH SOUTHERN AND ANTARTIC LANDS, TERRITORY OF THE FRENCH OVERSEAS TERRITORY)',
  FS: 'MICRONESIA, FEDERATED STATES OF',
  GO: 'GLORIOSO ISLANDS (FRENCH POSSESSION)',
  GB: 'GABON',
  GC: 'GREECE',
  GD: 'GEORGIA (FORMERLY GRUZINSKAYA)',
  GE: 'GERMANY (EAST GERMANY: 1945-1989 -EM)(WEST GERMANY: 1945-1989, SEE GW AND WG)',
  GF: 'GUERNSEY, BAILIWICK OF (BRITISH CROWN DEPENDENCY)',
  GG: 'GHANA',
  GI: 'GUINEA',
  GJ: 'GRENADA',
  GK: 'GAMBIA, THE',
  GN: 'GREENLAND',
  GP: 'GUADELOUPE, OVERSEAS DEPARTMENT OF FRANCE',
  GS: 'SOUTH GEORGIA AND SOUTH SANDWICH ISLANDS',
  GT: 'GUATEMALA',
  GY: 'GUYANA',
  GZ: 'GAZA',
  HC: 'HERZEGOVENIA (FOR REFERENCE ONLY) AND BOSNIA BP',
  HD: 'HONDURAS',
  HE: 'HEARD ISLAND AND MCDONALD ISLAND. TERRITORY OF (AUSTRALIAN EXTERNAL TERRITORY)',
  HK: 'HONG KONG',
  HN: 'VANUATU, REPUBLIC OF (FORMERLY NEW HEBRIDES)',
  HR: 'CHRISTMAS ISLAND, TERRITORY OF (AUSTRALIAN EXTERNAL TERRITORY)',
  HS: 'SAINT HELENA',
  HT: 'HAITI',
  HU: 'HUNGARY',
  IO: 'INDONESIA (INCLUDES PORTUESE TIMOR)',
  IB: 'MAN, ISLE OF (BRITISH CROWN DEPENDENCY)',
  IC: 'ICELAND',
  IE: 'IRELAND (DOES NOT INCLUDE NORTH IRELAND)',
  II: 'INDIA (INCLUDING SIKKIM-SK FOR REFERENCE ONLY)',
  IM: 'MADEIRA ISLANDS',
  IQ: 'IRAQ',
  IR: 'IRAN',
  IS: 'ISRAEL',
  IT: 'ITALY (INCLUDES SICILY AND SARDINIA)',
  IU: 'NIUE',
  IY: "IVORY COAST (COTE D'LVOIRE)",
  JO: 'JORDAN',
  JA: 'JAPAN',
  JE: 'JERSEY, BAILIWICK OF (BRITISH CROWN DEPENDENCY)',
  JM: 'JAMAICA',
  JN: 'JAN MAYEN (NORWEGIAN TERRITORY)',
  JU: 'JUAN DE NOVA ISLAND',
  KO: 'SOUTH KOREA',
  KB: 'KIRIBATI (FORMERLY GILBERT ISLANDS, ELLICE ISLAND SEE TV)',
  KC: 'CROATIA',
  KE: 'KENYA',
  KH: 'MANAHIKI ISLAND',
  KN: 'KOREA - NORTH KOREA',
  KT: 'KAZAKHSTAN',
  KU: 'KUWAIT',
  KV: 'KOSOVO',
  KZ: 'KYRGYZSTAN',
  LO: 'SLOVENIA',
  LB: 'LIBERIA',
  LD: 'MOLDOVA',
  LE: 'LESOTHO',
  LF: 'SLOVAKIA',
  LH: 'LITHUANIA',
  LI: 'LIECHTENSTEIN',
  LN: 'LEBANON',
  LS: 'LAOS',
  LT: 'LATVIA',
  LU: 'SAINT LUCIA',
  LX: 'LUXEMBOURG',
  LY: 'LIBYA',
  MF: 'MALAWI',
  MG: 'MONGOLIA',
  MJ: 'MONACO',
  ML: 'MALI',
  MM: 'MEXICO (SEE SEPARATE LIST OF MEXICAN STATES; USE CODE MM ONLY WHEN STATE IS UNKNOWN)',
  MP: 'MADAGASCAR (INCLUDED IN MALAGASY REPUBLIC)',
  MQ: 'MOROCCO',
  MU: 'MAURITANIA',
  MV: 'MALDIVES',
  MY: 'MALTA',
  MZ: 'MALAYSIA',
  NO: 'PAPAU NEW GUINEA (FORMERLY NEW GUINEA)',
  NE: 'NETHERLANDS (HOLLAND)',
  NF: 'LABRADOR (INCLUDED IN NEWFOUNDLAND)',
  NG: 'NIGERIA',
  NI: 'NORTHERN IRELAND (USE UN FOR UNITED KINGDOM)',
  NN: 'NIGER',
  NP: 'NEPAL',
  NQ: 'NEW CALEDONIA AND DEPENDENCIES, TERRITORY OF (FRENCH OVERSEAS TERRITORY)',
  NR: 'NAURA',
  NU: 'NICARAGUA',
  NW: 'NORWAY',
  NX: 'NETHERLANDS ANTILLES (BONAIRE AND CURACAO)',
  NZ: 'NEW ZEALAND',
  PO: 'POLAND',
  PC: 'PITCAIRN, HENDERSON, DUCIE, AND OENO ISLANDS (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  PD: 'PALAU, REPUBLIC OF',
  PF: 'PARACEL ISLANDS',
  PG: 'GUINEA-BISSAU (FORMERLY PORTUGUESE GUINEA)',
  PI: 'PHILIPPINES',
  PK: 'PAKISTAN',
  PM: 'PANAMA',
  PS: 'SAINT PIERRE AND MIQUELON, TERRITORIAL COLLECTIVITY OF',
  PT: 'PORTUGAL',
  PU: 'PERU',
  PV: 'PARAGUAY',
  QA: 'QATAR',
  RA: 'RUSSIA (FORMERLY USSR)',
  RB: 'CONGO, REPUBLIC OF (BRAZZAVILLE CAPTIAL)',
  RC: "CHINA - PEOPLE'S REPUBLIC OF CHINA",
  RE: 'REUNION, DEPARTMENT OF',
  RF: 'RUSSIAN FEDERATION',
  RG: 'GIBRALTAR (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  RH: 'ZIMBABWE, REPUBLIC OF (FORMERLY RHODESIA)',
  RR: 'MONTSERRAT (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  RS: 'WESTERN SAHARA (FORMERLY SPANISH SAHARA)',
  RU: 'ROMANIA/RUMANIA',
  RV: 'VIETNAM, SOCIALIST REPUBLIC OF (FORMERLY VIETNAM-VM REFERENCE ONLY)',
  RW: 'RWANDA',
  RY: "YEMEN, REPUBLIC OF (PEOPLE'S DEMOCRATIC REPUBLIC OF YEMEN-ST AND YEMEN ARAB REPUBLIC-YE, UNIFIED IN 1990)",
  SA: 'SIERRE LEONE (SIERRA LEONE)',
  SB: 'SAUDI ARABIA',
  SE: 'SEYCHELLES',
  SF: 'SOUTH AFRICA',
  SG: 'SENEGAL',
  SH: 'SAN MARINO',
  SJ: 'NAMIBIA (SOUTH-WEST AFRICA)',
  SM: 'SOMALIA',
  SP: 'SPAIN',
  SQ: 'SWEDEN',
  SR: 'SINGAPORE',
  SS: 'SCOTLAND OR UN FOR UNITED KINGDOM',
  SU: 'SUDAN',
  SV: 'SVALBARD (NORWEIGAN TERRITORY)',
  SW: 'SWAZILAND',
  SY: 'SYRIA',
  SZ: 'SWITZERLAND',
  TO: 'TOGO',
  TC: 'UNITED ARAB EMIRATES (FORMERLY TRUCIAL STATES)',
  TD: 'TRUST TERRITORY OF THE PACIFIC ISLANDS',
  TE: 'SPRATLY ISLANDS',
  TF: 'TUAMONTU ARCHIPELAGO',
  TG: 'TONGA',
  TH: 'THAILAND',
  TJ: 'TAJIKISTAN',
  TK: 'TOKELAU (NEW ZEALAND TERRITORY)',
  TM: 'TROMELIN ISLAND (FRENCH POSSESSION)',
  TP: 'SAO TOME AND PRINCIPE',
  TQ: 'TONGAREVA',
  TR: 'TURKS AND CACOS ISLANDS (DEPENDENT TERRITORY OF UNITED KINGDOM)',
  TS: 'SAINT CHRISTOPHER (OR SAINT KITTS) AND NEVIS',
  TT: 'TRINIDAD AND TOBAGO',
  TU: 'TUNISIA',
  TV: 'TUVALU (FORMERLY ELLICE ISLAND, GILBERT ISLANDS KB)',
  TW: 'TAIWAN, REPUBLIC OF CHINA',
  TY: 'TURKEY',
  TZ: 'TANZANIA, UNITED REPUBLIC OF',
  UG: 'UGANDA',
  UK: 'UKRAINE',
  UM: 'MAURITIUS',
  UN: 'UNITED KINGDOM (ENGLAND-EN, SCOTLAND-SS, WALES-WL, NORTH IRELAND-NI, GREAT BRITIAN FOR REFERENCE ONLY)',
  UR: 'TURKMENISTAN',
  US: 'USA - USED: 1. LIS FIELD OF PLATES ISSUED BY US GOVT, US MILITARY PLATES AND US CIVILIAN AIRCRAFT, 2. RES OF BOATS THAT ARE USCG DOCUMENTED, 3. GUN MAK FIELD UNKN, MFD IN US, NOT US MIL WEAPON, 4. III POB NAT AMER IF STATE IS UNKN',
  UV: 'BURKINA FASO (KNOWN AS BURKINA,FORMERLY UPPER VOLTA)',
  UY: 'URUGUAY',
  UZ: 'UZBEKISTAN, REPUBLIC OF',
  VB: 'BRITISH VIRGIN ISLANDS',
  VV: 'SAINT VINCENT AND THE GRENADINES',
  VY: 'VATICAN CITY',
  VZ: 'VENEZUELA, REPUBLIC OF',
  WB: 'WEST BANK',
  WF: 'WALLIS AND FUTUAN (FRENCH TERRITORY)(FRENCH OVERSEAS TERRITORY)',
  WL: 'WALES (ALSO UN FOR UNITED KINGDOM)',
  WN: 'WEST INDIES (FOR WEST INDIES ISLANDS NOT SEPARATELY LISTED) (PRE 1995 RES BOAT FILE CODE FOR WASHINGTON, SEE WA)',
  WS: 'WESTERN SAMOA (PRE 1995 RES BOAT FILE CODE FOR WISCONSIN, SEE WI)',
  XA: 'SERBIA',
  XB: 'MONTENEGRO',
  XX: 'UNKNOWN PLACE OF BIRTH (FOR USE IN III RECORDS ONLY)',
  YO: 'MAYOTTE, TERRITORIAL COLLECTIVITY OF',
  YG: 'YUGOSLAVIA',
  YY: 'UNLISTED (ANY FOREIGN COUNTRY/DEPENDENCY/TERRITORY NOT INCLUDED IN THE ABBREVIATION LIST)',
  ZO: 'MOZAMBIQUE',
  ZB: 'MARTINIQUE',
  ZC: 'SURINAM',
  ZD: 'MACEDONIA',
  ZF: 'SOUTH SUDAN',
  ZI: 'CANARY ISLANDS',
  ZM: 'ZAMBIA, REPUBLIC OF (FORMERLY CONGO KINSHASA)',
  ZR: 'CONGO, DEMOCRATIC REPUBLIC OF, CAPITAL KINSHASA (FORMERLY ZAIRE)',
} as const;

/**
 * Friendly display names for countries from NCIC Section 7.1.
 * Maps NCIC code -> friendly name (used for dropdown labels and display).
 * The stored DB value remains the NCIC description from CountryList.
 */
export const CountryDisplayNames: Record<string, string> = {
  AA: 'Albania',
  AD: 'Andorra',
  AE: 'Anguilla (dependent territory of United Kingdom)',
  AF: 'Afghanistan',
  AH: 'Ashmore and Cartier Islands, Territory of (Australian external territory)',
  AI: 'Antigua and Barbuda (formerly Antigua)',
  AJ: 'Aruba (now independent of Netherlands Antilles)',
  AN: 'Algeria',
  AO: 'Angola',
  AP: 'Armenia',
  AQ: 'Azores Islands',
  AS: 'Australia',
  AT: 'Argentina',
  AU: 'Austria',
  AV: 'Azerbaijan',
  BB: 'Barbados',
  BD: 'Bahamas, The',
  BE: 'Bahrain/Bahrein',
  BF: 'Bassas Da India (French possession)',
  BG: 'Belgium',
  BH: 'Belize (formerly British Honduras)',
  BI: 'Burundi',
  BL: 'Bangladesh',
  BM: 'Bermuda (dependent territory of United Kingdom)',
  BN: 'Bhutan',
  BO: 'British Indian Ocean Territory (dependent territory of United Kingdom)',
  BP: 'Bosnia and Herzegovina',
  BQ: 'Bouvet Island (Norwegian territory)',
  BR: 'Burma',
  BS: 'Solomon Islands (formerly British Solomon Islands)',
  BT: 'Botswana',
  BU: 'Bulgaria',
  BV: 'Bolivia',
  BW: 'Balearic Islands',
  BX: 'Brunei',
  BY: 'Belarus',
  BZ: 'Brazil',
  CB: 'Colombia, Republic of',
  CC: 'Cuba, Republic of',
  CD: 'Canada (use CD only when province is unknown)',
  CF: 'Chad',
  CJ: 'Cambodia (formerly Khmer Republic & Kampuchea)',
  CM: 'Cameroon',
  CP: 'Cayman Islands (dependent territory of United Kingdom)',
  CQ: 'Chile, Republic of',
  CR: 'Costa Rica, Republic of',
  CS: 'Cyprus, Republic of',
  CV: 'Cape Verde Islands',
  CW: 'Central African Republic',
  CY: 'Sri Lanka (formerly Ceylon)',
  DB: 'Clipperton Island (French possession)',
  DD: 'Cocos (Keeling) Islands, Territory of (Australian territory)',
  DG: 'Comoros, Federal Islamic Republic of the',
  DH: 'Benin (formerly Dahomey)',
  DI: 'Cook Islands',
  DJ: 'Coral Sea Islands, Territory of (Australian external territory)',
  DK: 'Denmark, Kingdom of',
  DM: 'Dominica',
  DN: 'Djibouti, Republic of',
  DR: 'Dominican Republic',
  EK: 'Equatorial Guinea',
  EL: 'El Salvador',
  EN: 'England',
  EO: 'Ethiopia',
  ER: 'Europa Island (French possession)',
  ES: 'Estonia',
  ET: 'Eritrea',
  EU: 'Ecuador',
  EY: 'Egypt (formerly United Arab Republic)',
  EZ: 'Czech Republic',
  FA: 'Falkland Islands, Colony of the (Islas Malvinas)',
  FD: 'Finland',
  FG: 'French Guiana (Department of Guiana)',
  FJ: 'Fiji',
  FN: 'France',
  FO: 'Faroe Islands',
  FP: 'French Polynesia, Territory of (French overseas territory)',
  FR: 'French Southern and Antarctic Lands, Territory of the (French overseas territory)',
  FS: 'Micronesia, Federated States of',
  GB: 'Gabon',
  GC: 'Greece',
  GD: 'Georgia (formerly Gruzinskaya)',
  GE: 'Germany',
  GF: 'Guernsey, Bailiwick of (British Crown dependency)',
  GG: 'Ghana',
  GI: 'Guinea',
  GJ: 'Grenada',
  GK: 'Gambia, The',
  GN: 'Greenland',
  GO: 'Glorioso Islands (French possession)',
  GP: 'Guadeloupe, Department of',
  GS: 'South Georgia and the South Sandwich Islands',
  GT: 'Guatemala',
  GY: 'Guyana',
  GZ: 'Gaza',
  HC: 'Herzegovina (for reference only) and Bosnia BP',
  HD: 'Honduras',
  HE: 'Heard Island and McDonald Islands, Territory of (Australian external territory)',
  HK: 'Hong Kong',
  HN: 'Vanuatu, Republic of (formerly New Hebrides)',
  HR: 'Christmas Island, Territory of (Australian external territory)',
  HS: 'Saint Helena (dependent territory of United Kingdom)',
  HT: 'Haiti',
  HU: 'Hungary',
  IB: 'Isle of Man (British Crown dependency)',
  IC: 'Iceland',
  IE: 'Ireland (does not include Northern Ireland)',
  II: 'India',
  IM: 'Madeira Islands',
  IO: 'Indonesia (includes Portuguese Timor)',
  IQ: 'Iraq',
  IR: 'Iran',
  IS: 'Israel',
  IT: 'Italy (includes Sicily and Sardinia)',
  IU: 'Niue',
  IY: "Cote d'Ivoire, Republic of (Ivory Coast)",
  JA: 'Japan',
  JE: 'Jersey, Bailiwick of (British Crown dependency)',
  JM: 'Jamaica',
  JN: 'Jan Mayen (Norwegian territory)',
  JO: 'Jordan',
  JU: 'Juan de Nova Island',
  KB: 'Kiribati (formerly Gilbert Islands)',
  KC: 'Croatia',
  KE: 'Kenya',
  KH: 'Manahiki Island',
  KN: 'Korea - North Korea',
  KO: 'Korea - South Korea',
  KT: 'Kazakhstan',
  KU: 'Kuwait',
  KV: 'Kosovo',
  KZ: 'Kyrgyzstan',
  LB: 'Liberia',
  LD: 'Moldova',
  LE: 'Lesotho',
  LF: 'Slovakia',
  LH: 'Lithuania',
  LI: 'Liechtenstein',
  LN: 'Lebanon',
  LO: 'Slovenia',
  LS: 'Laos',
  LT: 'Latvia',
  LU: 'Saint Lucia',
  LX: 'Luxembourg',
  LY: 'Libya',
  MF: 'Malawi',
  MG: 'Mongolia',
  MJ: 'Monaco',
  ML: 'Mali',
  MM: 'Mexico (use MM only when state is unknown)',
  MP: 'Madagascar (included in Malagasy Republic)',
  MQ: 'Morocco',
  MU: 'Mauritania',
  MV: 'Maldives',
  MY: 'Malta',
  MZ: 'Malaysia',
  NE: 'Netherlands (Holland)',
  NF: 'Labrador (included in Newfoundland)',
  NG: 'Nigeria',
  NI: 'Northern Ireland',
  NN: 'Niger',
  NO: 'Papua New Guinea (formerly New Guinea)',
  NP: 'Nepal',
  NQ: 'New Caledonia and Dependencies, Territory of (French overseas territory)',
  NR: 'Nauru',
  NU: 'Nicaragua',
  NW: 'Norway',
  NX: 'Netherlands Antilles (Bonaire and Curacao)',
  NZ: 'New Zealand',
  OC: 'Macau (formerly Macao)',
  OF: 'Norfolk Island, Territory of (Australian external territory)',
  OI: 'Okinawa',
  OM: 'Oman',
  PC: 'Pitcairn, Henderson, Ducie, and Oeno Islands (dependent territory of United Kingdom)',
  PD: 'Palau, Republic of',
  PF: 'Paracel Islands',
  PG: 'Guinea-Bissau (formerly Portuguese Guinea)',
  PI: 'Philippines',
  PK: 'Pakistan',
  PM: 'Panama',
  PO: 'Poland',
  PS: 'Saint Pierre and Miquelon, Territorial Collectivity of',
  PT: 'Portugal',
  PU: 'Peru',
  PV: 'Paraguay',
  QA: 'Qatar',
  RA: 'Russia (formerly USSR)',
  RB: 'Congo, Republic of (Brazzaville capital)',
  RC: "China - People's Republic of China",
  RE: 'Reunion, Department of',
  RF: 'Russian Federation',
  RG: 'Gibraltar (dependent territory of United Kingdom)',
  RH: 'Zimbabwe, Republic of (formerly Rhodesia)',
  RR: 'Montserrat (dependent territory of United Kingdom)',
  RS: 'Western Sahara (formerly Spanish Sahara)',
  RU: 'Romania/Rumania',
  RV: 'Vietnam, Socialist Republic of',
  RW: 'Rwanda',
  RY: 'Yemen, Republic of',
  SA: 'Sierra Leone',
  SB: 'Saudi Arabia',
  SE: 'Seychelles',
  SF: 'South Africa',
  SG: 'Senegal',
  SH: 'San Marino',
  SJ: 'Namibia (South-West Africa)',
  SM: 'Somalia',
  SP: 'Spain',
  SQ: 'Sweden',
  SR: 'Singapore',
  SS: 'Scotland',
  SU: 'Sudan',
  SV: 'Svalbard (Norwegian territory)',
  SW: 'Swaziland',
  SY: 'Syria',
  SZ: 'Switzerland',
  TC: 'United Arab Emirates (formerly Trucial States)',
  TD: 'Trust Territory of the Pacific Islands',
  TE: 'Spratly Islands',
  TF: 'Tuamotu Archipelago',
  TG: 'Tonga',
  TH: 'Thailand',
  TJ: 'Tajikistan',
  TK: 'Tokelau (New Zealand territory)',
  TM: 'Tromelin Island (French possession)',
  TO: 'Togo',
  TP: 'Sao Tome and Principe',
  TQ: 'Tongareva',
  TR: 'Turks and Caicos Islands (dependent territory of United Kingdom)',
  TS: 'Saint Christopher (or Saint Kitts) and Nevis',
  TT: 'Trinidad and Tobago',
  TU: 'Tunisia',
  TV: 'Tuvalu (formerly Ellice Islands)',
  TW: 'Taiwan, Republic of China',
  TY: 'Turkey',
  TZ: 'Tanzania, United Republic of',
  UG: 'Uganda',
  UK: 'Ukraine',
  UM: 'Mauritius',
  UN: 'United Kingdom',
  UR: 'Turkmenistan',
  US: 'United States of America (USA)',
  UV: 'Burkina Faso (formerly Upper Volta)',
  UY: 'Uruguay',
  UZ: 'Uzbekistan, Republic of',
  VB: 'British Virgin Islands',
  VV: 'Saint Vincent and the Grenadines',
  VY: 'Vatican City',
  VZ: 'Venezuela, Republic of',
  WB: 'West Bank',
  WF: 'Wallis and Futuna, Territory of the (French overseas territory)',
  WL: 'Wales',
  WN: 'West Indies (for West Indies Islands not separately listed)',
  WS: 'Western Samoa',
  XA: 'Serbia',
  XB: 'Montenegro',
  XX: 'Unknown Place of Birth',
  YG: 'Yugoslavia',
  YO: 'Mayotte, Territorial Collectivity of',
  YY: 'All others (not in listing)',
  ZB: 'Martinique',
  ZC: 'Surinam',
  ZD: 'Macedonia',
  ZF: 'South Sudan',
  ZI: 'Canary Islands',
  ZM: 'Zambia, Republic of',
  ZO: 'Mozambique',
  ZR: 'Congo, Democratic Republic of (formerly Zaire)',
} as const;

export const NationalityList = {
  AFGHAN: 'Afghan',
  ALBANIAN: 'Albanian',
  ALGERIAN: 'Algerian',
  AMERICAN: 'American',
  ANDORRAN: 'Andorran',
  ANGOLAN: 'Angolan',
  ANGUILLAN: 'Anguillan',
  ARGENTINE: 'Argentine',
  ARMENIAN: 'Armenian',
  AUSTRALIAN: 'Australian',
  AUSTRIAN: 'Austrian',
  AZERBAIJANI: 'Azerbaijani',
  BAHAMIAN: 'Bahamian',
  BAHRAINI: 'Bahraini',
  BANGLADESHI: 'Bangladeshi',
  BARBADIAN: 'Barbadian',
  BELARUSIAN: 'Belarusian',
  BELGIAN: 'Belgian',
  BELIZEAN: 'Belizean',
  BENINESE: 'Beninese',
  BERMUDIAN: 'Bermudian',
  BHUTANESE: 'Bhutanese',
  BOLIVIAN: 'Bolivian',
  BOTSWANAN: 'Botswanan',
  BRAZILIAN: 'Brazilian',
  BRITISH: 'British',
  BRITISH_VIRGIN_ISLANDER: 'British Virgin Islander',
  BRUNEIAN: 'Bruneian',
  BULGARIAN: 'Bulgarian',
  BURKINAN: 'Burkinan',
  BURMESE: 'Burmese',
  BURUNDIAN: 'Burundian',
  CAMBODIAN: 'Cambodian',
  CAMEROONIAN: 'Cameroonian',
  CANADIAN: 'Canadian',
  CAPE_VERDEAN: 'Cape Verdean',
  CAYMAN_ISLANDER: 'Cayman Islander',
  CENTRAL_AFRICAN: 'Central African',
  CHADIAN: 'Chadian',
  CHILEAN: 'Chilean',
  CHINESE: 'Chinese',
  CITIZEN_OF_ANTIGUA_AND_BARBUDA: 'Citizen of Antigua and Barbuda',
  CITIZEN_OF_BOSNIA_AND_HERZEGOVINA: 'Citizen of Bosnia and Herzegovina',
  CITIZEN_OF_GUINEA_BISSAU: 'Citizen of Guinea-Bissau',
  CITIZEN_OF_KIRIBATI: 'Citizen of Kiribati',
  CITIZEN_OF_SEYCHELLES: 'Citizen of Seychelles',
  CITIZEN_OF_THE_DOMINICAN_REPUBLIC: 'Citizen of the Dominican Republic',
  CITIZEN_OF_VANUATU: 'Citizen of Vanuatu',
  COLOMBIAN: 'Colombian',
  COMORAN: 'Comoran',
  CONGOLESE_CONGO: 'Congolese (Congo)',
  CONGOLESE_DRC: 'Congolese (DRC)',
  COOK_ISLANDER: 'Cook Islander',
  COSTA_RICAN: 'Costa Rican',
  CROATIAN: 'Croatian',
  CUBAN: 'Cuban',
  CYMRAES: 'Cymraes',
  CYMRO: 'Cymro',
  CYPRIOT: 'Cypriot',
  CZECH: 'Czech',
  DANISH: 'Danish',
  DJIBOUTIAN: 'Djiboutian',
  DOMINICAN: 'Dominican',
  DUTCH: 'Dutch',
  EAST_TIMORESE: 'East Timorese',
  ECUADOREAN: 'Ecuadorean',
  EGYPTIAN: 'Egyptian',
  EMIRATI: 'Emirati',
  ENGLISH: 'English',
  EQUATORIAL_GUINEAN: 'Equatorial Guinean',
  ERITREAN: 'Eritrean',
  ESTONIAN: 'Estonian',
  ETHIOPIAN: 'Ethiopian',
  FAROESE: 'Faroese',
  FIJIAN: 'Fijian',
  FILIPINO: 'Filipino',
  FINNISH: 'Finnish',
  FRENCH: 'French',
  GABONESE: 'Gabonese',
  GAMBIAN: 'Gambian',
  GEORGIAN: 'Georgian',
  GERMAN: 'German',
  GHANAIAN: 'Ghanaian',
  GIBRALTARIAN: 'Gibraltarian',
  GREEK: 'Greek',
  GREENLANDIC: 'Greenlandic',
  GRENADIAN: 'Grenadian',
  GUAMANIAN: 'Guamanian',
  GUATEMALAN: 'Guatemalan',
  GUINEAN: 'Guinean',
  GUYANESE: 'Guyanese',
  HAITIAN: 'Haitian',
  HONDURAN: 'Honduran',
  HONG_KONGER: 'Hong Konger',
  HUNGARIAN: 'Hungarian',
  ICELANDIC: 'Icelandic',
  INDIAN: 'Indian',
  INDONESIAN: 'Indonesian',
  IRANIAN: 'Iranian',
  IRAQI: 'Iraqi',
  IRISH: 'Irish',
  ISRAELI: 'Israeli',
  ITALIAN: 'Italian',
  IVORIAN: 'Ivorian',
  JAMAICAN: 'Jamaican',
  JAPANESE: 'Japanese',
  JORDANIAN: 'Jordanian',
  KAZAKH: 'Kazakh',
  KENYAN: 'Kenyan',
  KITTITIAN: 'Kittitian',
  KOSOVAN: 'Kosovan',
  KUWAITI: 'Kuwaiti',
  KYRGYZ: 'Kyrgyz',
  LAO: 'Lao',
  LATVIAN: 'Latvian',
  LEBANESE: 'Lebanese',
  LIBERIAN: 'Liberian',
  LIBYAN: 'Libyan',
  LIECHTENSTEIN_CITIZEN: 'Liechtenstein citizen',
  LITHUANIAN: 'Lithuanian',
  LUXEMBOURGER: 'Luxembourger',
  MACANESE: 'Macanese',
  MACEDONIAN: 'Macedonian',
  MALAGASY: 'Malagasy',
  MALAWIAN: 'Malawian',
  MALAYSIAN: 'Malaysian',
  MALDIVIAN: 'Maldivian',
  MALIAN: 'Malian',
  MALTESE: 'Maltese',
  MARSHALLESE: 'Marshallese',
  MARTINIQUAIS: 'Martiniquais',
  MAURITANIAN: 'Mauritanian',
  MAURITIAN: 'Mauritian',
  MEXICAN: 'Mexican',
  MICRONESIAN: 'Micronesian',
  MOLDOVAN: 'Moldovan',
  MONEGASQUE: 'Monegasque',
  MONGOLIAN: 'Mongolian',
  MONTENEGRIN: 'Montenegrin',
  MONTSERRATIAN: 'Montserratian',
  MOROCCAN: 'Moroccan',
  MOSOTHO: 'Mosotho',
  MOZAMBICAN: 'Mozambican',
  NAMIBIAN: 'Namibian',
  NAURUAN: 'Nauruan',
  NEPALESE: 'Nepalese',
  NEW_ZEALANDER: 'New Zealander',
  NICARAGUAN: 'Nicaraguan',
  NIGERIAN: 'Nigerian',
  NIGERIEN: 'Nigerien',
  NIUEAN: 'Niuean',
  NORTH_KOREAN: 'North Korean',
  NORTHERN_IRISH: 'Northern Irish',
  NORWEGIAN: 'Norwegian',
  OMANI: 'Omani',
  PAKISTANI: 'Pakistani',
  PALAUAN: 'Palauan',
  PALESTINIAN: 'Palestinian',
  PANAMANIAN: 'Panamanian',
  PAPUA_NEW_GUINEAN: 'Papua New Guinean',
  PARAGUAYAN: 'Paraguayan',
  PERUVIAN: 'Peruvian',
  PITCAIRN_ISLANDER: 'Pitcairn Islander',
  POLISH: 'Polish',
  PORTUGUESE: 'Portuguese',
  PRYDEINIG: 'Prydeinig',
  PUERTO_RICAN: 'Puerto Rican',
  QATARI: 'Qatari',
  ROMANIAN: 'Romanian',
  RUSSIAN: 'Russian',
  RWANDAN: 'Rwandan',
  SALVADOREAN: 'Salvadorean',
  SAMMARINESE: 'Sammarinese',
  SAMOAN: 'Samoan',
  SAO_TOMEAN: 'Sao Tomean',
  SAUDI_ARABIAN: 'Saudi Arabian',
  SCOTTISH: 'Scottish',
  SENEGALESE: 'Senegalese',
  SERBIAN: 'Serbian',
  SIERRA_LEONEAN: 'Sierra Leonean',
  SINGAPOREAN: 'Singaporean',
  SLOVAK: 'Slovak',
  SLOVENIAN: 'Slovenian',
  SOLOMON_ISLANDER: 'Solomon Islander',
  SOMALI: 'Somali',
  SOUTH_AFRICAN: 'South African',
  SOUTH_KOREAN: 'South Korean',
  SOUTH_SUDANESE: 'South Sudanese',
  SPANISH: 'Spanish',
  SRI_LANKAN: 'Sri Lankan',
  ST_HELENIAN: 'St Helenian',
  ST_LUCIAN: 'St Lucian',
  STATELESS: 'Stateless',
  SUDANESE: 'Sudanese',
  SURINAMESE: 'Surinamese',
  SWAZI: 'Swazi',
  SWEDISH: 'Swedish',
  SWISS: 'Swiss',
  SYRIAN: 'Syrian',
  TAIWANESE: 'Taiwanese',
  TAJIK: 'Tajik',
  TANZANIAN: 'Tanzanian',
  THAI: 'Thai',
  TOGOLESE: 'Togolese',
  TONGAN: 'Tongan',
  TRINIDADIAN: 'Trinidadian',
  TRISTANIAN: 'Tristanian',
  TUNISIAN: 'Tunisian',
  TURKISH: 'Turkish',
  TURKMEN: 'Turkmen',
  TURKS_AND_CAICOS_ISLANDER: 'Turks and Caicos Islander',
  TUVALUAN: 'Tuvaluan',
  UGANDAN: 'Ugandan',
  UKRAINIAN: 'Ukrainian',
  URUGUAYAN: 'Uruguayan',
  UZBEK: 'Uzbek',
  VATICAN_CITIZEN: 'Vatican citizen',
  VENEZUELAN: 'Venezuelan',
  VIETNAMESE: 'Vietnamese',
  VINCENTIAN: 'Vincentian',
  WALLISIAN: 'Wallisian',
  WELSH: 'Welsh',
  YEMENI: 'Yemeni',
  ZAMBIAN: 'Zambian',
  ZIMBABWEAN: 'Zimbabwean',
} as const;

export const LanguageList = {
  AF: 'Afrikaans',
  SQ: 'Albanian',
  AR: 'Arabic',
  HY: 'Armenian',
  EU: 'Basque',
  BN: 'Bengali',
  BG: 'Bulgarian',
  CA: 'Catalan',
  KM: 'Cambodian',
  ZH: 'Chinese (Mandarin)',
  HR: 'Croatian',
  CS: 'Czech',
  DA: 'Danish',
  NL: 'Dutch',
  EN: 'English',
  ET: 'Estonian',
  FJ: 'Fiji',
  FI: 'Finnish',
  FR: 'French',
  KA: 'Georgian',
  DE: 'German',
  EL: 'Greek',
  GU: 'Gujarati',
  HE: 'Hebrew',
  HI: 'Hindi',
  HU: 'Hungarian',
  IS: 'Icelandic',
  ID: 'Indonesian',
  GA: 'Irish',
  IT: 'Italian',
  JA: 'Japanese',
  JW: 'Javanese',
  KO: 'Korean',
  LA: 'Latin',
  LV: 'Latvian',
  LT: 'Lithuanian',
  MK: 'Macedonian',
  MS: 'Malay',
  ML: 'Malayalam',
  MT: 'Maltese',
  MI: 'Maori',
  MR: 'Marathi',
  MN: 'Mongolian',
  NE: 'Nepali',
  NO: 'Norwegian',
  FA: 'Persian',
  PL: 'Polish',
  PT: 'Portuguese',
  PA: 'Punjabi',
  QU: 'Quechua',
  RO: 'Romanian',
  RU: 'Russian',
  SM: 'Samoan',
  SR: 'Serbian',
  SK: 'Slovak',
  SL: 'Slovenian',
  ES: 'Spanish',
  SW: 'Swahili',
  SV: 'Swedish',
  TA: 'Tamil',
  TT: 'Tatar',
  TE: 'Telugu',
  TH: 'Thai',
  BO: 'Tibetan',
  TO: 'Tonga',
  TR: 'Turkish',
  UK: 'Ukrainian',
  UR: 'Urdu',
  UZ: 'Uzbek',
  VI: 'Vietnamese',
  CY: 'Welsh',
  XH: 'Xhosa',
} as const;

export const eyeColorInput = {
  name: 'eyeColor',
  type: 'autocomplete',
  label: 'Eye color',
  items: [
    ...Object.entries(EyeColor).map(([key, val]) => ({
      label: `${key} - ${
        val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()
      }`,
      value: val,
    })),
  ],
};

export const genderInput = {
  name: 'sex',
  type: 'autocomplete',
  label: 'Sex',
  items: [
    {
      label: GenderList[Gender.MALE],
      value: Gender.MALE,
    },
    {
      label: GenderList[Gender.FEMALE],
      value: Gender.FEMALE,
    },
    {
      label: GenderList[Gender.UNKNOWN],
      value: Gender.UNKNOWN,
    },
  ],
  rules: {
    required: false,
  },
};

export const phoneNumberInput = {
  name: 'phoneNumber',
  type: 'pattern',
  label: 'Phone number',
  format: '(###) ###-####',
  rules: {
    pattern: {
      value: /[2-9][0-9]{9}$/im,
      // value: /[2-9]{1}[0-9]{9}$/im,
      message: 'Invalid phone number.',
    },
  },
};

export const phoneTypeInput = {
  type: 'autocomplete',
  name: 'type',
  label: 'Number type',
  items: [
    {
      label: 'Home',
      value: PhoneNumberType.HOME,
    },
    {
      label: 'Mobile',
      value: PhoneNumberType.MOBILE,
    },
    {
      label: 'Work',
      value: PhoneNumberType.WORK,
    },
    {
      label: 'Caller',
      value: PhoneNumberType.CALLER,
    },
    {
      label: 'Other',
      value: PhoneNumberType.OTHER,
    },
  ],
  grid: {
    md: 4,
  },
};

export const maritalInput = {
  name: 'maritalStatus',
  type: 'autocomplete',
  label: 'Marital status',
  grid: {
    md: 3.01,
  },
  items: [
    ...Object.entries(MaritalStatus).map(([key, val]) => ({
      label: `${key} - ${val}`,
      value: val,
    })),
  ],
};

export const hairColorInput = {
  name: 'hairColor',
  type: 'autocomplete',
  label: 'Hair color',
  items: [
    ...Object.entries(HairColor).map(([key, val]) => ({
      label: `${key} - ${val}`,
      value: val,
    })),
  ],
};

export const religionInput = {
  name: 'religion',
  type: 'autocomplete',
  label: 'Religion',
  grid: {
    md: 3.01,
  },
  items: [
    ...Object.entries(ReligionList).map(([key, val]) => ({
      label: `${key} - ${val}`,
      value: val,
    })),
  ],
};

export const educationInput = {
  name: 'education',
  type: 'autocomplete',
  label: 'Education',
  grid: {
    md: 3.01,
  },
  items: [
    ...Object.entries(EducationList).map(([key, val]) => ({
      label: `${key} - ${val}`,
      value: val,
    })),
  ],
};

export const citizenOfInput = {
  name: 'citizenOf',
  type: 'autocomplete',
  label: 'Citizen of',
  grid: {
    md: 3.01,
  },
  items: [
    // Pin USA to top
    {
      label: CountryDisplayNames.US,
      value: CountryList.US,
    },
    // Sort remaining countries alphabetically by display name
    ...Object.entries(CountryDisplayNames)
      .filter(([code]) => code !== 'US')
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([code, displayName]) => ({
        label: displayName,
        value: CountryList[code as keyof typeof CountryList],
      })),
  ],
};

export const languageInput = {
  name: 'spokenLanguages',
  type: 'autocomplete',
  label: 'Languages spoken',
  multiple: true,
  grid: {
    md: 3.01,
  },
  items: [
    ...Object.entries(LanguageList).map(([value, label]) => ({
      label,
      value,
    })),
  ],
};
